// src/api.js
import axios from 'axios';

const API = axios.create({
  baseURL: '/api',
});

let pendingReauthPromise = null;
let pendingReauthResolve = null;
let pendingReauthReject = null;

function openReauthModal() {
  if (!pendingReauthPromise) {
    pendingReauthPromise = new Promise((resolve, reject) => {
      pendingReauthResolve = resolve;
      pendingReauthReject = reject;
      window.dispatchEvent(new CustomEvent('visitoms:reauth-required'));
    }).finally(() => {
      pendingReauthPromise = null;
      pendingReauthResolve = null;
      pendingReauthReject = null;
    });
  }

  return pendingReauthPromise;
}

export function resolvePendingReauth(value = true) {
  if (pendingReauthResolve) {
    pendingReauthResolve(value);
  }
}

export function rejectPendingReauth(error = new Error('Re-auth cancelled')) {
  if (pendingReauthReject) {
    pendingReauthReject(error);
  }
}

/**
 * Helper: rollback iz impersonate moda nazad na originalnog usera (super admin)
 */
function rollbackImpersonationIfAny() {
  try {
    const impToken = localStorage.getItem('impersonator_token');
    const impUser = localStorage.getItem('impersonator_user');

    if (impToken && impUser) {
      localStorage.setItem('token', impToken);
      localStorage.setItem('user', impUser);

      localStorage.removeItem('impersonator_token');
      localStorage.removeItem('impersonator_user');
      localStorage.removeItem('location');

      window.location.href = '/super';
      return true;
    }
  } catch (e) {
    console.warn('Error during impersonation rollback', e);
  }
  return false;
}

/**
 * Helper: klasičan logout (bez impersonate)
 */
export function hardLogoutToLogin(sessionExpired = false) {
  try {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    localStorage.removeItem('location');
    localStorage.removeItem('impersonator_token');
    localStorage.removeItem('impersonator_user');
  } catch (e) {
    console.warn('Error clearing localStorage on logout', e);
  }

  if (sessionExpired) {
    window.location.href = '/session-expired';
  } else {
    const path = window.location.pathname || '';
    if (!path.startsWith('/login') && !path.startsWith('/magic-login')) {
      window.location.href = '/login';
    }
  }
}

export async function apiGet(url, params) {
  const res = await API.get(url, { params });
  return res.data;
}
export async function apiPost(url, body) {
  const res = await API.post(url, body);
  return res.data;
}
export async function apiPut(url, body) {
  const res = await API.put(url, body);
  return res.data;
}

// -----------------------------------------------------
// Request interceptor – dodavanje tokena ako postoji
// -----------------------------------------------------
API.interceptors.request.use((req) => {
  const token = localStorage.getItem('token');
  const url = req.url || '';

  const isPublicAuthRoute =
    url.includes('/login') ||
    url.includes('/auth/magic-login') ||
    url.includes('/auth/teacher-magic-request') ||
    url.includes('/auth/parent-magic-request');

  const isPublicVisitConfirm =
    url.includes('/visits/') && url.includes('/confirm');

  if (!isPublicAuthRoute && !isPublicVisitConfirm) {
    if (token) {
      req.headers = req.headers || {};
      if (!req.headers.Authorization) {
        req.headers.Authorization = token;
      }
    }
  }

  return req;
});

// -----------------------------------------------------
// Response interceptor – centralno hvatanje 401
// -----------------------------------------------------
API.interceptors.response.use(
  (response) => response,
  async (error) => {
    const status = error.response?.status;
    const data = error.response?.data || {};
    const code = data.code;
    const originalRequest = error.config || {};
    const url = originalRequest.url || '';

    const hadAuthHeader =
      originalRequest.headers && originalRequest.headers.Authorization;

    const isReauthEndpoint = url.includes('/auth/reauth');

    if (
      status === 401 &&
      code === 'REAUTH_REQUIRED' &&
      hadAuthHeader &&
      !originalRequest._retryAfterReauth &&
      !isReauthEndpoint
    ) {
      try {
        await openReauthModal();

        originalRequest._retryAfterReauth = true;
        originalRequest.headers = originalRequest.headers || {};

        const freshToken = localStorage.getItem('token');
        if (freshToken) {
          originalRequest.headers.Authorization = freshToken;
        }

        return API(originalRequest);
      } catch {
        return Promise.reject(error);
      }
    }

    if (status === 401 && hadAuthHeader) {
      const rolledBack = rollbackImpersonationIfAny();

      if (!rolledBack) {
        if (code === 'TOKEN_EXPIRED') {
          hardLogoutToLogin(true);
        } else {
          hardLogoutToLogin(false);
        }
      }
    }

    return Promise.reject(error);
  }
);

// -----------------------------------------------------
// Login s geolokacijom + Organization key + trusted device
// -----------------------------------------------------
export const login = (
  email,
  password,
  latitude,
  longitude,
  organizationKey,
  trustedDeviceToken,
  deviceLabel,
  platform
) => {
  const payload = {
    email,
    password,
    latitude,
    longitude,
    trusted_device_token: trustedDeviceToken,
    device_label: deviceLabel,
    platform,
  };

  if (organizationKey && organizationKey.trim()) {
    payload.organization_key = organizationKey.trim();
  }

  return API.post('/login', payload);
};

// Ostatak helpera
export const getStudents = (token) =>
  API.get('/students', {
    headers: {
      Authorization: token,
    },
  });

export const getTeachers = () => API.get('/teachers');

export const createVisit = (student_id, scheduled_at, notes) =>
  API.post('/visits', { student_id, scheduled_at, notes });

export const sendGeolocation = (token, lat, lon, type = 'other') =>
  API.post(
    '/geolocation',
    { lat, lon, type },
    {
      headers: { Authorization: token },
    }
  );

export const reverseGeocode = (token, lat, lon) =>
  API.post(
    '/reverse-geocode',
    { lat, lon },
    {
      headers: { Authorization: token },
    }
  );

export const getTaxonomy = (active = 1) =>
  API.get('/taxonomy', { params: { active } });

export const saveTeacherTaxonomy = (teacherId, payload) =>
  API.put(`/teachers/${teacherId}/taxonomy`, payload);

export const saveStudentTaxonomy = (studentId, payload) =>
  API.put(`/students/${studentId}/taxonomy`, payload);

export const matchTeachers = (studentId) =>
  API.get('/match/teachers', { params: { student_id: studentId } });

export default API;


